// backtestReturns.ts — the Backtest results' two "how did THIS actually perform"
// headline numbers, both reusing existing pure building blocks (fundAnalytics.ts's cagr
// for the time-weighted figure, lib/irr.ts's bisection for the money-weighted one) rather
// than inventing new math. Kept UI-agnostic (a SimulationResult + the fund's cached
// prices in, plain numbers out) so both are independently unit-testable against
// hand-derived fixtures, without a component/DOM.
import type { SimulationResult } from '@etf-cockpit/engine';
import { cagr } from './fundAnalytics.js';
import type { PricePoint } from './fundAnalytics.js';
import { irrBisection } from './irr.js';
import type { Cashflow } from './irr.js';

/** The fund's own time-weighted annualized PRICE return over [usedFrom, usedTo] — "what
 * did the FUND do", independent of the Sparplan's contribution timing/amounts. Delegates
 * entirely to fundAnalytics.ts's cagr() (same 365.25-day annualization, same
 * null-when-too-little-data honesty — never a fabricated number) so there is exactly one
 * CAGR implementation in the app. */
export function backtestPriceCagr(prices: PricePoint[], usedFrom: string, usedTo: string): number | null {
  return cagr(prices, usedFrom, usedTo);
}

/** Builds the actual investor cashflow series from a computed backtest result: every
 * `contribution` event's full GROSS amount (execution fee included — that IS money that
 * left the investor's pocket, not just the invested portion) as a negative flow on its
 * date, plus the final net wealth as one positive flow on the series' last date. Exported
 * (not just used internally) so it's independently testable against a hand-built
 * SimulationResult fixture, separately from the IRR bisection itself. */
export function backtestCashflows(result: SimulationResult): Cashflow[] {
  const flows: Cashflow[] = [];
  for (const event of result.events) {
    if (event.type === 'contribution') {
      flows.push({ date: event.date, amountCents: -event.grossCents });
    }
  }
  const lastDate = result.series[result.series.length - 1]?.date;
  if (lastDate) {
    flows.push({ date: lastDate, amountCents: result.waterfall.netWealthCents });
  }
  return flows;
}

/** Money-weighted annualized return (IRR) of the actual Sparplan cashflows — "what did
 * THIS INVESTOR'S money actually earn", accounting for contribution timing (unlike the
 * time-weighted price CAGR above, two identical-looking funds can have different IRRs for
 * the same investor depending on when the money went in). `null` when there's nothing to
 * bracket (see lib/irr.ts's irrBisection doc) — e.g. a scenario with no contributions and
 * no lump sum at all. */
export function backtestMoneyWeightedReturn(result: SimulationResult): number | null {
  return irrBisection(backtestCashflows(result));
}
